/* ==========================================================================
   PV Product Section (pv-product-section.js)
   - Image gallery (arrows + thumbnails)
   - Countdown timer (fixed duration per page load, loops at zero)
   - Variant picker → syncs buy button, refill note and ATC form
   - AJAX add-to-cart with mini-cart refresh/open
   - Ritual upsell block: one-click add of the Ritual bundle
   ========================================================================== */
(function () {
  'use strict';

  function qs(sel, root) {
    return (root || document).querySelector(sel);
  }
  function qsa(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  /* -----------------------------
     Gallery
     ----------------------------- */
  function initGallery(root) {
    var track = qs('[data-pv-track]', root);
    var slides = qsa('[data-pv-slide]', root);
    if (!track || slides.length < 2) return;

    var thumbsWrap = qs('[data-pv-thumbs]', root);
    var thumbs = qsa('[data-pv-thumb]', root);
    var current = 0;

    /* Eager-load the remaining slides so they are ready when slid into view
       (lazy images inside an overflow-hidden track never intersect). */
    qsa('img[loading="lazy"]', track).forEach(function (img) {
      img.loading = 'eager';
    });

    /* Center active thumb in the horizontal slide strip (mobile always;
       desktop when thumbs use the slide-pane layout). Avoids scrollIntoView
       so the page itself does not jump. */
    function centerActiveThumb(thumb) {
      if (!thumb || !thumbsWrap) return;
      var isSlidePane =
        thumbsWrap.classList.contains('pv-product__thumbs--slide') ||
        thumbsWrap.scrollWidth > thumbsWrap.clientWidth + 1;
      if (!isSlidePane) return;

      var thumbCenter = thumb.offsetLeft + thumb.offsetWidth / 2;
      var target = thumbCenter - thumbsWrap.clientWidth / 2;
      var maxScroll = Math.max(0, thumbsWrap.scrollWidth - thumbsWrap.clientWidth);
      var left = Math.max(0, Math.min(target, maxScroll));

      if (typeof thumbsWrap.scrollTo === 'function') {
        thumbsWrap.scrollTo({ left: left, behavior: 'smooth' });
      } else {
        thumbsWrap.scrollLeft = left;
      }
    }

    function goTo(index) {
      current = (index + slides.length) % slides.length;
      track.style.transform = 'translateX(-' + current * 100 + '%)';
      slides.forEach(function (slide, i) {
        slide.classList.toggle('is-active', i === current);
        // Only the visible video plays; the rest pause off-screen.
        var video = slide.querySelector('video');
        if (!video) return;
        if (i === current) {
          var p = video.play();
          if (p && p.catch) p.catch(function () {});
        } else {
          video.pause();
        }
      });
      thumbs.forEach(function (thumb, i) {
        thumb.classList.toggle('is-active', i === current);
      });
      centerActiveThumb(thumbs[current]);
    }

    var prev = qs('[data-pv-prev]', root);
    var next = qs('[data-pv-next]', root);
    if (prev) prev.addEventListener('click', function () { goTo(current - 1); });
    if (next) next.addEventListener('click', function () { goTo(current + 1); });

    thumbs.forEach(function (thumb, i) {
      thumb.addEventListener('click', function () { goTo(i); });
    });

    /* Touch swipe on the main image */
    var touchStartX = null;
    var touchStartY = null;
    track.addEventListener('touchstart', function (e) {
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
    }, { passive: true });
    track.addEventListener('touchend', function (e) {
      if (touchStartX === null) return;
      var dx = e.changedTouches[0].clientX - touchStartX;
      var dy = e.changedTouches[0].clientY - touchStartY;
      touchStartX = null;
      touchStartY = null;
      if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
      goTo(dx < 0 ? current + 1 : current - 1);
    }, { passive: true });
  }

  window.PVProduct = window.PVProduct || {};
  window.PVProduct.reinitGallery = function (root) {
    initGallery(root || document);
  };

  /* -----------------------------
     Countdown timers
     ----------------------------- */
  function initTimers(root) {
    qsa('[data-pv-timer]', root).forEach(function (timer) {
      var display = qs('[data-pv-timer-display]', timer);
      if (!display) return;

      var total = parseInt(timer.getAttribute('data-pv-duration') || '0', 10);
      if (!total || total < 1) total = 583; /* 00:09:43 fallback */
      var remaining = total;

      function pad(n) {
        return n < 10 ? '0' + n : String(n);
      }

      function tick() {
        if (remaining <= 0) remaining = total;
        var h = Math.floor(remaining / 3600);
        var m = Math.floor((remaining % 3600) / 60);
        var sec = remaining % 60;
        display.textContent = pad(h) + ':' + pad(m) + ':' + pad(sec);
        remaining--;
      }

      tick();
      setInterval(tick, 1000);
    });
  }

  /* -----------------------------
     Variant picker → form/button sync
     ----------------------------- */
  function syncFromCard(root, card) {
    if (!card) return;

    var attr = function (name) {
      return (card.getAttribute(name) || '').trim();
    };

    var variantId = attr('data-variant-id');
    var sellingPlan = attr('data-selling-plan');

    var idInput = qs('[data-pv-form-id]', root);
    var spInput = qs('[data-pv-form-sp]', root);
    if (idInput && /^\d+$/.test(variantId)) idInput.value = variantId;
    if (spInput) spInput.value = /^\d+$/.test(sellingPlan) ? sellingPlan : '';

    var pairs = [
      ['[data-pv-btn-compare]', attr('data-compare-total')],
      ['[data-pv-btn-price]', attr('data-price-total')],
      ['[data-pv-note-compare]', attr('data-refill-compare')],
      ['[data-pv-note-price]', attr('data-refill-price')],
      ['[data-pv-note-days]', attr('data-refill-days')]
    ];
    pairs.forEach(function (pair) {
      var el = qs(pair[0], root);
      if (el) el.textContent = pair[1];
    });

    var pct = parseInt(attr('data-save-percent'), 10);
    if (isNaN(pct)) pct = 0;

    var badge = qs('[data-pv-gallery-badge]', root);
    if (badge) {
      if (pct > 0) {
        var prefix = (badge.getAttribute('data-badge-prefix') || 'save').trim();
        badge.textContent = (prefix ? prefix + ' ' : '') + pct + '%';
        badge.hidden = false;
      } else {
        badge.hidden = true;
      }
    }

    try {
      var countdownPct = qs('[data-pv-countdown-percent]', root);
      if (countdownPct) countdownPct.textContent = pct > 0 ? String(pct) : '';
    } catch (e) { /* never block variant / buy-box sync */ }

    var optionIndex = qsa('[data-pv-variant-card]', root).indexOf(card) + 1;
    syncShippingLine(root, optionIndex);
  }

  /* Per-option shipping line in small-benefits blocks: each block carries
     data-pv-shipping-1..3 texts; the line for the selected option (1-based)
     is shown, an empty text hides it (e.g. no free shipping on 1 jar). */
  function syncShippingLine(root, optionIndex) {
    if (optionIndex < 1) return;
    qsa('[data-pv-shipping-list]', root).forEach(function (list) {
      var block = list.closest('[data-pv-shipping-1]');
      if (!block) return;

      var html = (block.getAttribute('data-pv-shipping-' + optionIndex) || '').trim();
      if (html !== '' && html.indexOf('<') === -1) {
        html = '<ul><li>' + html + '</li></ul>';
      }
      list.innerHTML = html;
      list.hidden = html === '';
    });
  }

  function initPicker(root) {
    var cards = qsa('[data-pv-variant-card]', root);
    if (!cards.length) return;

    function select(card) {
      cards.forEach(function (c) {
        var active = c === card;
        c.classList.toggle('is-active', active);
        c.setAttribute('aria-selected', active ? 'true' : 'false');
      });
      syncFromCard(root, card);
    }

    cards.forEach(function (card) {
      card.addEventListener('click', function () { select(card); });
    });

    var initial = qs('[data-pv-variant-card].is-active', root) || cards[0];
    select(initial);
  }

  /* -----------------------------
     Ingredients popup
     ----------------------------- */
  function initIngredientsModal(root) {
    var modal = qs('[data-pv-ingredients-modal]', root);
    if (!modal) return;

    /* Portal the modal to <body>: ancestors like .pv-shell__inner create
       stacking contexts that would trap it under the sticky header. */
    if (modal.parentElement !== document.body) {
      document.body.appendChild(modal);
    }

    function open() {
      modal.hidden = false;
      document.body.style.overflow = 'hidden';
    }

    function close() {
      modal.hidden = true;
      document.body.style.overflow = '';
    }

    /* Delegated: works even if the gallery DOM is re-rendered (theme editor). */
    root.addEventListener('click', function (e) {
      if (e.target.closest('[data-pv-ingredients-open]')) {
        e.preventDefault();
        open();
      }
    });

    /* Close clicks bound on the modal itself — it no longer lives inside root. */
    modal.addEventListener('click', function (e) {
      if (e.target.closest('[data-pv-modal-close]')) close();
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !modal.hidden) close();
    });
  }

  /* -----------------------------
     Mini-cart helpers
     ----------------------------- */
  function qsDrawer(root) {
    return (root || document).querySelector('cart-drawer#mini-cart') ||
           (root || document).querySelector('cart-drawer[section="mini-cart"]');
  }

  function getMiniCartSectionId() {
    var drawer = qsDrawer(document);
    return (drawer && drawer.getAttribute('section')) || 'mini-cart';
  }

  function normalizeMiniCartSections(sections) {
    if (!sections || sections['mini-cart']) return sections;
    var keys = Object.keys(sections);
    if (keys.length !== 1) return sections;
    var normalized = {};
    normalized['mini-cart'] = sections[keys[0]];
    return normalized;
  }

  function buildCartPayload(cart, sections) {
    var payload = Object.assign({}, cart);
    if (sections) payload.sections = normalizeMiniCartSections(sections);
    return payload;
  }

  /* Authoritative cart totals. /cart/add.js only returns the line item(s) just
     added, so item_count for the header bubble has to come from /cart.js.
     Resolves to null on failure so a network hiccup here cannot break the
     mini-cart open that follows. */
  function fetchCartState() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .catch(function () { return null; });
  }

  function openMiniCartDrawer() {
    var drawer = qsDrawer(document);
    if (!drawer) return;
    try { drawer.open = true; } catch (e) { /* noop */ }
    drawer.setAttribute('open', '');
  }

  function openMiniCartHard(cart, sections) {
    var drawer = qsDrawer(document);
    var detail = { openMiniCart: true };

    /* The header count (.nch__cart-count) listens for cart:refresh on <html>
       and reads detail.cart.item_count — same contract as
       snippets/js-variables.liquid. Without detail.cart it ignores the event,
       which left the bubble stale until a page reload. */
    if (cart) detail.cart = buildCartPayload(cart, sections);

    try {
      var ev = new CustomEvent('cart:refresh', { bubbles: true, detail: detail });
      if (drawer) {
        drawer.dispatchEvent(ev);
      } else {
        document.documentElement.dispatchEvent(ev);
      }
      document.dispatchEvent(ev);
      window.dispatchEvent(ev);
    } catch (e) { /* noop */ }

    openMiniCartDrawer();
  }

  /* -----------------------------
     Add to cart
     ----------------------------- */
  function addItemsToCart(items) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: items,
        sections: getMiniCartSectionId(),
        sections_url: window.location.pathname
      })
    }).then(function (res) {
      return res.json().then(function (body) {
        if (!res.ok) throw body;
        return body;
      });
    });
  }

  function initAtc(root) {
    var form = qs('form[data-pv-atc-form]', root);
    if (!form) return;

    var inflight = false;

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      e.stopPropagation();
      if (inflight) return;

      var activeCard = qs('[data-pv-variant-card].is-active', root);
      if (activeCard) syncFromCard(root, activeCard);

      var idInput = qs('input[name="id"]', form);
      var spInput = qs('input[name="selling_plan"]', form);
      var variantId = idInput ? String(idInput.value || '').trim() : '';
      var spRaw = spInput ? String(spInput.value || '').trim() : '';

      if (!/^\d+$/.test(variantId)) {
        console.error('[PV Product] Missing/invalid variant id');
        return;
      }

      var item = { id: Number(variantId), quantity: 1 };
      if (/^\d+$/.test(spRaw)) item.selling_plan = Number(spRaw);

      var giveawayEnabled = root.dataset.giveawayBundleEnabled === 'true';
      // Tag the main line item itself so the orphan-gift cleanup can run
      // anywhere on the site (any page, any time) purely by reading
      // /cart.js — it no longer needs to know this product's id from a
      // [data-pv-product] element that may not exist on the current page.
      if (giveawayEnabled) {
        item.properties = { '_giveaway_main': 'true' };
      }

      var btn = qs('button[type="submit"]', form);
      if (btn) {
        btn.disabled = true;
        btn.setAttribute('aria-busy', 'true');
      }
      inflight = true;

      // Giveaway bundle: the main product is always added FIRST, on its
      // own, so (a) its cart line is created before the gift lines — this
      // guarantees the display order in the cart (main product, then
      // gifts) — and (b) the real purchase never depends on the gifts
      // succeeding at all. The 2 free-gift placeholder items are added in
      // a second, separate request; if that fails (e.g. a gift placeholder
      // is out of stock) it only logs a warning and never blocks checkout.
      // See GIVEAWAY-COORDINATION.md for the mechanism's known limits.
      addItemsToCart([item])
        .then(function (mainResult) {
          if (!giveawayEnabled) return mainResult;

          return fetch('/cart.js', { headers: { Accept: 'application/json' } })
            .then(function (r) { return r.json(); })
            .then(function (cart) {
              var existingGiftKeys = {};
              if (cart && cart.items) {
                cart.items.forEach(function (line) {
                  var giftKey = line.properties && line.properties['_giveaway_gift'];
                  if (giftKey) existingGiftKeys[giftKey] = true;
                });
              }

              var giftItems = [];
              var ebookVariantId = parseInt(root.dataset.giveawayEbookVariantId, 10);
              var entryVariantId = parseInt(root.dataset.giveawayEntryVariantId, 10);

              if (ebookVariantId && !existingGiftKeys.ebook) {
                giftItems.push({
                  id: ebookVariantId,
                  quantity: 1,
                  properties: { '_giveaway_gift': 'ebook', 'Gift': 'Free E-book' }
                });
              }
              if (entryVariantId && !existingGiftKeys.entry) {
                giftItems.push({
                  id: entryVariantId,
                  quantity: 1,
                  properties: { '_giveaway_gift': 'entry', 'Gift': 'Garmin Venu 4 Giveaway Entry' }
                });
              }

              if (giftItems.length === 0) return mainResult;

              return addItemsToCart(giftItems)
                .catch(function (err) {
                  console.warn('[PV Product] Giveaway gifts failed to add (main product was added successfully)', err);
                  return mainResult;
                });
            });
        })
        .then(function (addBody) {
          return fetchCartState().then(function (cart) {
            openMiniCartHard(cart, addBody.sections);
          });
        })
        .catch(function (err) {
          console.error('[PV Product ATC ERROR]', err);
        })
        .finally(function () {
          inflight = false;
          if (btn) {
            btn.disabled = false;
            btn.removeAttribute('aria-busy');
          }
        });
    });
  }

  /* -----------------------------
     Ritual upsell (ritual_upsell block)
     The Ritual bundle is a different product than this PDP, so it can't go
     through the main ATC form — it's added by its own variant/selling plan
     (data attributes on the block) and then reuses the same mini-cart
     refresh/open flow as the main buy button.
     ----------------------------- */
  var RITUAL_ADDED_STATE_MS = 1400;

  function setRitualButtonState(btn, state) {
    btn.classList.remove('is-loading', 'is-added');
    btn.disabled = false;
    btn.removeAttribute('aria-busy');

    if (state === 'loading') {
      btn.classList.add('is-loading');
      btn.disabled = true;
      btn.setAttribute('aria-busy', 'true');
      btn.textContent = btn.dataset.labelLoading || btn.dataset.labelDefault;
    } else if (state === 'added') {
      btn.classList.add('is-added');
      btn.textContent = btn.dataset.labelAdded || btn.dataset.labelDefault;
    } else {
      btn.textContent = btn.dataset.labelDefault;
    }
  }

  function initRitualUpsell(root) {
    /* Delegated: survives block re-renders in the theme editor. */
    root.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-pv-ritual-upsell-cta]');
      if (!btn || btn.disabled) return;

      var block = btn.closest('[data-pv-ritual-upsell]');
      var variantId = block ? String(block.dataset.variantId || '').trim() : '';
      var spRaw = block ? String(block.dataset.sellingPlan || '').trim() : '';

      if (!/^\d+$/.test(variantId)) {
        console.error('[PV Ritual Upsell] Missing/invalid variant id');
        return;
      }

      var item = { id: Number(variantId), quantity: 1 };
      if (/^\d+$/.test(spRaw)) item.selling_plan = Number(spRaw);

      setRitualButtonState(btn, 'loading');

      addItemsToCart([item])
        .then(function (addBody) {
          setRitualButtonState(btn, 'added');
          return fetchCartState().then(function (cart) {
            openMiniCartHard(cart, addBody.sections);
          });
        })
        .then(function () {
          window.setTimeout(function () {
            setRitualButtonState(btn, 'default');
          }, RITUAL_ADDED_STATE_MS);
        })
        .catch(function (err) {
          console.warn('[PV Ritual Upsell] Failed to add Ritual bundle to cart:', err);
          setRitualButtonState(btn, 'default');
        });
    });
  }

  /* -----------------------------
     Bootstrap
     ----------------------------- */
  // Orphaned free-gift cleanup (removing the gifts if the main gated
  // product is removed from the cart) lives in the separate, globally
  // loaded assets/pv-giveaway-cleanup.js — not here — because this file
  // is only ever included on the PDP itself (see the <script> tag in
  // sections/pv-product-section.liquid), so anything defined here can
  // never run on other pages (cart page, collection quick-add, etc.)
  // where the shopper can just as easily remove the main item.
  function initSection(root) {
    if (root.dataset.pvProductInit === 'true') return;
    root.dataset.pvProductInit = 'true';
    initGallery(root);
    initTimers(root);
    initPicker(root);
    initAtc(root);
    initIngredientsModal(root);
    initRitualUpsell(root);
  }

  function initAll() {
    qsa('[data-pv-product]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (e) {
    qsa('[data-pv-product]', e.target).forEach(initSection);
  });
})();
